import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges} from '@angular/core';
import {RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';

interface ErrorConfig {
  icon: string;
  titleKey: string;
  descriptionKey: string;
}

const errorConfigs: Record<string, ErrorConfig> = {
  '400': {
    icon: '@phi.warning',
    titleKey: 'features.errorPage.400.title',
    descriptionKey: 'features.errorPage.400.description',
  },
  '403': {
    icon: '@phi.lock',
    titleKey: 'features.errorPage.403.title',
    descriptionKey: 'features.errorPage.403.description',
  },
  '404': {
    icon: '@phi.magnifying-glass',
    titleKey: 'features.errorPage.404.title',
    descriptionKey: 'features.errorPage.404.description',
  },
  '500': {
    icon: '@phi.warning-octagon',
    titleKey: 'features.errorPage.500.title',
    descriptionKey: 'features.errorPage.500.description',
  },
  '503': {
    icon: '@phi.cloud-slash',
    titleKey: 'features.errorPage.503.title',
    descriptionKey: 'features.errorPage.503.description',
  },
};

const fallbackConfig: ErrorConfig = {
  icon: '@phi.warning',
  titleKey: 'features.errorPage.fallback.title',
  descriptionKey: 'features.errorPage.fallback.description',
};

@Component({
  selector: 'gatewee-error-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    GateweeIconComponent,
    GateweeButtonComponent,
  ],
  templateUrl: './gatewee-error-page.component.html',
  styleUrls: ['./gatewee-error-page.component.css'],
})
export class GateweeErrorPageComponent implements OnChanges {
  /** HTTP status code — drives all defaults. Accepts number or string. */
  @Input() code: string | number = '404';

  /** Router link for the CTA button. */
  @Input() ctaLink = '/panel';

  /** Override the CTA label i18n key. Falls back to shared key. */
  @Input() ctaKey = 'features.errorPage.cta';

  /** Override the icon. Defaults to code-specific icon. */
  @Input() icon?: string;

  /** Override the title i18n key. Defaults to code-specific key. */
  @Input() titleKey?: string;

  /** Override the description i18n key. Defaults to code-specific key. */
  @Input() descriptionKey?: string;

  // ── Resolved values, computed in ngOnChanges ──────────────────────────────

  resolvedIcon = fallbackConfig.icon;
  resolvedTitleKey = fallbackConfig.titleKey;
  resolvedDescriptionKey = fallbackConfig.descriptionKey;
  resolvedCode = '404';

  ngOnChanges(): void {
    const key = String(this.code);
    const config = errorConfigs[key] ?? fallbackConfig;

    this.resolvedCode = key;
    this.resolvedIcon = this.icon ?? config.icon;
    this.resolvedTitleKey = this.titleKey ?? config.titleKey;
    this.resolvedDescriptionKey = this.descriptionKey ?? config.descriptionKey;
  }
}
